import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { MessageSquare, AlertCircle, Plus, Edit, Trash2, Clock } from 'lucide-react';
import { Loader } from './LoadingSpinner';
import SectionHeader from './SectionHeader';
import { TextField, DatePicker, Autocomplete } from '@anyemp/ui-kit';
import Avatar from './Avatar';
import { talents, jobApplications, users } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import ConfirmDialog from './ConfirmDialog';

const Communication = forwardRef(({
  entityId,
  entityType = 'talent', // 'talent' або 'job-application'
  talentType,
  onRefresh,
  showAddButton = true,
  showEditButtons = true
}, ref) => {
  const { hasPermission, user } = useAuth();

  const commResource = useMemo(() => {
    if (entityType === 'talent' && talentType) {
      return `${talentType}_communication`;
    }
    return 'job_application';
  }, [entityType, talentType]);

  const canCreate = useMemo(() => hasPermission('talent-service', commResource, 'create'), [hasPermission, commResource]);
  const canUpdate = useMemo(() => hasPermission('talent-service', commResource, 'update'), [hasPermission, commResource]);
  const canDelete = useMemo(() => hasPermission('talent-service', commResource, 'delete'), [hasPermission, commResource]);

  const [communications, setCommunications] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // null = closed | { id: 'new' } = add form at top | { id: X, ... } = edit inline
  const [editingCommunication, setEditingCommunication] = useState(null);
  const [formData, setFormData] = useState({
    manager_id: '',
    notes: '',
    follow_up_date: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // communication to delete
  const usersLoadedRef = useRef(false);

  const handleCreate = () => {
    fetchUsers();
    setEditingCommunication({ id: 'new' });
    // По умолчанию менеджер — текущий пользователь; в форме его можно сменить
    setFormData({ manager_id: user?.id || '', notes: '', follow_up_date: '' });
  };

  useImperativeHandle(ref, () => ({
    openForm: handleCreate
  }));

  useEffect(() => {
    if (entityId) {
      fetchCommunications();
    }
  }, [entityId]);

  const fetchCommunications = async () => {
    if (!entityId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (entityType === 'talent') {
        response = await talents.getTalentCommunications(entityId);
      } else {
        response = await jobApplications.getJobApplicationCommunications(entityId);
      }
      
      if (response.success) {
        setCommunications(response.data);
      } else {
        setCommunications(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching communications:', err);
      setError('Error loading communications');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (usersLoadedRef.current) return;
    usersLoadedRef.current = true;
    try {
      const response = await users.getAllUsers();
      
      // Обрабатываем структуру ответа: response.data.users
      let usersData = [];
      if (response.success && response.data && response.data.users) {
        usersData = Array.isArray(response.data.users) ? response.data.users : [];
      } else if (response.data && Array.isArray(response.data)) {
        usersData = response.data;
      } else if (Array.isArray(response)) {
        usersData = response;
      }
      
      setUsersList(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsersList([]); // Устанавливаем пустой массив в случае ошибки
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCommunication?.id !== 'new') {
        // Update existing communication
        if (entityType === 'talent') {
          await talents.updateTalentCommunication(entityId, editingCommunication.id, formData);
        } else {
          await jobApplications.updateJobApplicationCommunication(entityId, editingCommunication.id, formData);
        }
      } else {
        // Create new communication
        if (entityType === 'talent') {
          await talents.addTalentCommunication(entityId, formData);
        } else {
          await jobApplications.addJobApplicationCommunication(entityId, formData);
        }
      }

      setEditingCommunication(null);
      setFormData({ manager_id: '', notes: '', follow_up_date: '' });
      fetchCommunications();
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error saving communication:', err);
      alert('Error saving communication');
    }
  };

  const handleEdit = (communication) => {
    fetchUsers();
    setEditingCommunication(communication);
    setFormData({
      manager_id: communication.manager_id,
      notes: communication.notes || '',
      follow_up_date: communication.follow_up_date ?
        new Date(communication.follow_up_date).toISOString().split('T')[0] : ''
    });
  };

  const handleDelete = (communication) => {
    setDeleteConfirm(communication);
  };

  const confirmDelete = async () => {
    try {
      if (entityType === 'talent') {
        await talents.deleteTalentCommunication(entityId, deleteConfirm.id);
      } else {
        await jobApplications.deleteJobApplicationCommunication(entityId, deleteConfirm.id);
      }

      fetchCommunications();

      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error deleting communication:', err);
      alert('Error deleting communication');
    }
  };

  const handleCancel = () => {
    setEditingCommunication(null);
    setFormData({ manager_id: '', notes: '', follow_up_date: '' });
  };

  const isOverdue = (followUpDate) => {
    if (!followUpDate) return false;
    return new Date(followUpDate) < new Date();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  return (
    <>
    <div className={showAddButton ? 'p-6 bg-light-bg-primary dark:bg-dark-bg-primary' : ''}>
      {showAddButton && (
        <SectionHeader
          icon={MessageSquare}
          title={communications && communications.length > 0 ? `Communications (${communications.length})` : 'Communications'}
          action={canCreate && (
            <button onClick={handleCreate} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors">
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          )}
        />
      )}

      {error && (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-400 dark:text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchCommunications}
            className="mt-2 px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Add form — only for new items, appears at the top */}
      {editingCommunication?.id === 'new' && (
        <div className="mb-3">
          <CommForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            usersList={usersList}
            isEdit={false}
          />
        </div>
      )}

      {/* Communications List */}
      {communications && communications.length > 0 ? (
        <div className="space-y-3">
          {communications.map((communication, index) => (
            editingCommunication?.id === communication.id ? (
              /* Edit form replaces the card inline */
              <CommForm
                key={communication.id || index}
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                usersList={usersList}
                isEdit
              />
            ) : (
              <div key={communication.id || index} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      {communication.created_at && (
                        <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary flex items-center gap-1">
                          <Clock className="w-3 h-3" />{formatDateTime(communication.created_at)}
                        </span>
                      )}
                      {communication.follow_up_date && (
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ${
                          isOverdue(communication.follow_up_date)
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}>
                          {isOverdue(communication.follow_up_date) ? 'Overdue' : 'Scheduled'}
                        </span>
                      )}
                    </div>

                    {communication.manager && (
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar
                          avatar={communication.manager.avatar}
                          name={communication.manager.name}
                          email={communication.manager.email}
                          size="sm"
                          showInitialOnMissing
                          initialBgClassName="bg-light-bg-secondary dark:bg-dark-bg-secondary"
                          iconBgClassName="bg-light-bg-secondary dark:bg-dark-bg-secondary"
                          initialTextClassName="text-light-text-secondary dark:text-dark-text-secondary font-medium"
                          iconClassName="text-light-text-secondary dark:text-dark-text-secondary"
                        />
                        <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                          {communication.manager.name || `ID: ${communication.manager_id}`}
                        </span>
                      </div>
                    )}

                    {communication.notes && (
                      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary whitespace-pre-wrap break-words">{communication.notes}</p>
                    )}

                    {communication.follow_up_date && (
                      <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Follow-up: {formatDate(communication.follow_up_date)}
                      </p>
                    )}
                  </div>

                  {(canUpdate || canDelete) && showEditButtons && (
                    <div className="flex items-center gap-1 shrink-0">
                      {canUpdate && (
                        <button onClick={() => handleEdit(communication)} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors" title="Edit">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(communication)} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
        </div>
      ) : (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary py-4">No communications yet</p>
      )}

      {/* Overdue alert */}
      {communications && communications.some(comm => comm.follow_up_date && isOverdue(comm.follow_up_date)) && (
        <div className="mt-4 border border-red-200 dark:border-red-800 rounded-lg p-3 bg-red-50 dark:bg-red-900/20 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">Some communications require follow-up contact.</p>
        </div>
      )}
    </div>
    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      confirmStyle="danger"
      title="Delete Communication"
      message="Are you sure you want to delete this communication? This action cannot be undone."
      confirmText="Delete"
    />
    </>
  );
});

const CommForm = ({ formData, setFormData, onSubmit, onCancel, usersList, isEdit }) => (
  <div className="border border-primary-300 dark:border-primary-700 rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Autocomplete
          clearable
          fullWidth
          label="Manager *"
          value={formData.manager_id != null ? String(formData.manager_id) : ''}
          onChange={(value) => setFormData(prev => ({ ...prev, manager_id: value ? Number(value) : '' }))}
          options={Array.isArray(usersList) ? usersList.map(u => ({
            value: String(u.id),
            label: u.name || u.email || u.full_name || `User ${u.id}`
          })) : []}
          placeholder="Select manager"
        />
        <DatePicker
          label="Follow-up Date"
          size="small"
          fullWidth
          value={formData.follow_up_date ? new Date(formData.follow_up_date) : null}
          onChange={(d) => setFormData(prev => ({ ...prev, follow_up_date: d ? d.toISOString().slice(0, 10) : '' }))}
        />
      </div>
      <div>
        <TextField
          label="Notes"
          required
          multiline
          rows={3}
          size="small"
          fullWidth
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Enter communication notes..."
        />
      </div>
      <div className="flex gap-2">
        <button type="submit" className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors">
          {isEdit ? 'Save' : 'Add'}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 border border-light-border-primary dark:border-dark-border-primary text-sm text-light-text-secondary dark:text-dark-text-secondary rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors">
          Cancel
        </button>
      </div>
    </form>
  </div>
);

export default Communication;
